import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { useCart } from "../hooks/useCart";

/**
 * Full-page loader that immediately POSTs to /ink-shop/checkout and
 * redirects to the Stripe hosted-checkout URL. Users only see this
 * for a beat between clicking "Checkout" on /cart and hitting Stripe.
 *
 * Deliberately does NOT collect address here — Stripe Checkout collects
 * billing + shipping address (with card validation) more reliably than
 * a home-grown form.
 */
export default function CheckoutPage() {
  const { items } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState("");
  // useRef prevents Strict-Mode double invocations (React 18 dev)
  // from creating two Stripe sessions.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    if (items.length === 0) {
      navigate("/cart");
      return;
    }
    const country = window.localStorage.getItem("last.ink_country") || "GB";
    const method = params.get("m") || "";
    if (!method) {
      setError("Missing shipping method. Go back to your cart and pick one.");
      return;
    }
    api
      .post("/ink-shop/checkout", {
        country: country === "OTHER" ? "XX" : country,
        shipping_method_id: method,
        items: items.map((it) => ({
          product_id: it.product_id,
          variant_id: it.variant_id,
          quantity: it.quantity,
        })),
        return_origin: window.location.origin,
      })
      .then((r) => {
        if (!r.data?.url) throw new Error("No checkout URL returned");
        window.location.href = r.data.url;
      })
      .catch((e) => {
        setError(
          e?.response?.data?.detail ||
          "Couldn't start checkout. Please try again — no card charged.",
        );
      });
  }, [items, params, navigate]);

  return (
    <div className="min-h-screen bg-[var(--brand-bg,#F5F7FA)] text-[var(--brand-ink)]" data-testid="checkout-page">
      <Header />
      <main className="mx-auto grid max-w-md place-items-center px-4 py-24 text-center">
        {error ? (
          <>
            <p className="text-sm text-red-600" data-testid="checkout-error">{error}</p>
            <button
              type="button"
              onClick={() => navigate("/cart")}
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-xs font-semibold text-white"
              data-testid="checkout-back-to-cart"
            >
              Back to cart
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-6 w-6 animate-spin text-[var(--brand-body)]" />
            <p className="mt-3 text-sm text-[var(--brand-body)]">Handing you to Stripe securely…</p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
